/** @format */
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ServiceOverviewSearchProps } from '../../components/typescript'
import { TraceAnalysisListItem, TraceAnalysisSearchProps, FilterMtchProps } from '../typescript'
import {
  _$,
  Tab,
  Table,
  TableColumn,
  Tabs,
  Tag,
  TableSortValue,
  Pagination,
  dayjs
} from '@king-design/react'
import { useAsync } from 'react-use'
import { secondsFormat } from 'src/utils/common'
import { getTraceSpanList, getTraceRootSpanList } from 'src/api/ApiAIObservability'
import { capitalizeFirst } from 'src/utils/common'
import ExportTrace from './ExportTrace'
import ColumnConfigDropdown from './ColumnConfigDropdown'
import { useLocation, useHistory } from 'react-router-dom'
import { replaceSearchParams } from 'src/utils'
import variable from 'src/static/styles/var'
import { tokenValueFormat } from '../../constant'
import { TruncatedValueWithTooltip } from 'src/components'
import useWindowSize from 'src/hooks/useWindowSize'

// 状态码
export const renderStatusCode = (statusCode: string) => {
  switch (statusCode) {
    case 'OK':
      return <Tag type="success">OK</Tag>
    case 'ERROR':
      return <Tag type="danger">Error</Tag>
    default:
      return <Tag>{capitalizeFirst(statusCode)}</Tag>
  }
}
interface TraceTableProps {
  className?: string
  serviceName?: string
  headerSearch: ServiceOverviewSearchProps
  traceSearch: TraceAnalysisSearchProps
  setTraceSearch: (search: TraceAnalysisSearchProps) => void
  spanSearch: FilterMtchProps[]
  region?: string
}

export const TABLE_HEADERS = [
  { key: 'TraceId', value: _$('TraceID') },
  { key: 'Name', value: _$('Span名称') },
  { key: 'DurationMs', value: `${_$('耗时')}（ms）` },
  { key: 'ModelName', value: _$('模型名称') },
  { key: 'TotalTokens', value: _$('Total Tokens') },
  { key: 'InputTokens', value: _$('输入Token') },
  { key: 'OutputTokens', value: _$('输出Token') },
  { key: 'SpanKind', value: _$('Span类型') },
  { key: 'AISpanKind', value: _$('AI Span类型') },
  { key: 'Operation', value: _$('操作') },
  { key: 'StartTime', value: _$('开始时间') },
  { key: 'EndTime', value: _$('结束时间') },
  { key: 'StatusCode', value: _$('状态码') },
  { key: 'ServiceName', value: _$('服务名') },
  { key: 'SessionId', value: _$('会话ID') }
]
const TRACE_TABLE_LOCAL_STORAGE_KEY = 'ai-observability-trace-table-visible-headers'
export const DEFAULT_HEADER_KEYS = TABLE_HEADERS.map(item => item.key)

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}
const getStoredHeaderConfig = (): { checkedHeader: string[]; headerOrder: string[] } | null => {
  try {
    const raw = window.localStorage.getItem(TRACE_TABLE_LOCAL_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as {
      checkedHeader?: string[]
      headerOrder?: string[]
    }
    const validKeys = new Set(DEFAULT_HEADER_KEYS)
    const checkedHeader = (parsed.checkedHeader || []).filter(key => validKeys.has(key))
    const headerOrder = (parsed.headerOrder || []).filter(key => validKeys.has(key))
    if (!headerOrder.length) {
      return null
    }
    return {
      checkedHeader: checkedHeader.length ? checkedHeader : [...DEFAULT_HEADER_KEYS],
      headerOrder
    }
  } catch (error) {
    return null
  }
}

export type TableTabType = 'span' | 'rootSpan'

const TraceTable: React.FC<TraceTableProps> = ({
  className,
  serviceName,
  headerSearch,
  traceSearch,
  setTraceSearch,
  spanSearch,
  region
}) => {
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const runtime = query.get('runtime')
  const traceTableTab = query.get('traceTableTab')
  const [tab, setTab] = useState<TableTabType>((traceTableTab as TableTabType) || 'rootSpan')
  const [tableSort, setTableSort] = useState<TableSortValue>()
  const [checkedRow, setCheckedRow] = useState<string[]>([])
  const [checkedHeader, setCheckedHeader] = useState<string[]>(() => {
    if (typeof window === 'undefined') {
      return [...DEFAULT_HEADER_KEYS]
    }
    const storedConfig = getStoredHeaderConfig()
    return storedConfig?.checkedHeader || [...DEFAULT_HEADER_KEYS]
  })
  const [headerOrder, setHeaderOrder] = useState<string[]>(() => {
    if (typeof window === 'undefined') {
      return [...DEFAULT_HEADER_KEYS]
    }
    const storedConfig = getStoredHeaderConfig()
    return storedConfig?.headerOrder || [...DEFAULT_HEADER_KEYS]
  })
  const headerMap = useMemo(() => new Map(TABLE_HEADERS.map(item => [item.key, item])), [])
  const visibleHeaders = useMemo(() => {
    const headers = headerOrder
      .filter(key => checkedHeader.includes(key))
      .map(key => headerMap.get(key))
      .filter(Boolean)
    return headers
  }, [checkedHeader, headerMap, headerOrder])

  useEffect(() => {
    try {
      window.localStorage.setItem(
        TRACE_TABLE_LOCAL_STORAGE_KEY,
        JSON.stringify({
          checkedHeader,
          headerOrder
        })
      )
    } catch (error) {
      // ignore storage failures
    }
  }, [checkedHeader, headerOrder])

  const { value: traceData, loading } = useAsync(async () => {
    if (headerSearch?.time[0] && headerSearch?.time[1]) {
      const params = {
        ...traceSearch,
        StartTime: headerSearch?.time[0],
        EndTime: headerSearch?.time[1],
        Filters: [...spanSearch, ...traceSearch.Filters].filter(item => item?.Values?.length > 0),
        Order: tableSort?.type,
        OrderBy: tableSort?.key
      }
      setCheckedRow([])
      if (tab === 'span') {
        const result = await getTraceSpanList(params)
        return {
          list: result?.Spans || [],
          total: result?.TotalCount || 0
        }
      } else {
        const result = await getTraceRootSpanList(params)
        return {
          list: result?.RootSpans || [],
          total: result?.TotalCount || 0
        }
      }
    }
  }, [headerSearch, traceSearch, spanSearch, serviceName, tableSort, tab, region])

  // 跳转Trace详情
  const toTraceDetail = (row: TraceAnalysisListItem) => {
    const qs = new URLSearchParams({
      traceId: row.TraceId
    })
    let hashPath = ''
    if (location?.pathname.includes('/monitor/agentkit')) {
      hashPath = `/monitor/agentkit/runtimedetail/traceanDetail?${qs.toString()}&runtime=${runtime}`
    } else if (serviceName) {
      hashPath = `/monitor/aiobservability/servicedetail/traceanDetail?${qs.toString()}`
    } else {
      hashPath = `/monitor/aiobservability/traceanalysis/detail?${qs.toString()}`
    }
    history.push(hashPath)
  }

  // 跳转会话详情
  const toSessionDetail = (row: TraceAnalysisListItem) => {
    const qs = new URLSearchParams({
      sessionId: row.SessionId,
      serviceName: row.ServiceName,
      from: 'trace'
    })
    const hashPath = `/monitor/aiobservability/sessionanalysis/detail?${qs.toString()}`
    history.push(hashPath)
  }

  const { containerHeight: tableHeight } = useWindowSize({ offset: serviceName ? 185 : 136 })

  const renderTableColumn = (key: string) => {
    switch (key) {
      case 'TraceId':
        return (
          <TableColumn
            key="TraceId"
            title={_$('TraceID')}
            fixed="left"
            width="150"
            slotTemplate={([data]) => {
              return (
                <TruncatedValueWithTooltip value={data.TraceId} copyable>
                  <a
                    onClick={e => {
                      e.stopPropagation()
                      toTraceDetail(data)
                    }}
                  >
                    {data.TraceId}
                  </a>
                </TruncatedValueWithTooltip>
              )
            }}
          />
        )
      case 'Name':
        return (
          <TableColumn
            key="Name"
            title={_$('Span名称')}
            width="160"
            slotTemplate={([data]) => {
              return <TruncatedValueWithTooltip value={data.Name} />
            }}
          />
        )

      case 'ModelName':
        return (
          <TableColumn
            key="ModelName"
            title={_$('模型名称')}
            width="100"
            slotTemplate={([data]) => {
              return <div>{data.ModelName || '-'}</div>
            }}
          />
        )
      case 'TotalTokens':
        return (
          <TableColumn
            key="TotalTokens"
            title={_$('Total Tokens')}
            sortable
            width="100"
            slotTemplate={([data]) => {
              return tokenValueFormat(data.TotalTokens)
            }}
          />
        )
      case 'InputTokens':
        return (
          <TableColumn
            key="InputTokens"
            title={_$('输入Token')}
            sortable
            width="100"
            slotTemplate={([data]) => {
              return tokenValueFormat(data.InputTokens)
            }}
          />
        )
      case 'OutputTokens':
        return (
          <TableColumn
            key="OutputTokens"
            title={_$('输出Token')}
            sortable
            width="100"
            slotTemplate={([data]) => {
              return tokenValueFormat(data.OutputTokens)
            }}
          />
        )
      case 'StartTime':
        return (
          <TableColumn
            key="StartTime"
            title={_$('开始时间')}
            width="100"
            sortable
            slotTemplate={([data]) =>
              data.StartTime ? dayjs(data.StartTime).format('YYYY-MM-DD HH:mm:ss') : '-'
            }
          />
        )
      case 'EndTime':
        return (
          <TableColumn
            key="EndTime"
            title={_$('结束时间')}
            width="100"
            sortable
            slotTemplate={([data]) =>
              data.EndTime ? dayjs(data.EndTime).format('YYYY-MM-DD HH:mm:ss') : '-'
            }
          />
        )
      case 'DurationMs':
        return (
          <TableColumn
            key="DurationMs"
            title={_$('耗时')}
            width="100"
            sortable
            slotTemplate={([data]) =>
              data.DurationMs != null ? secondsFormat(data.DurationMs) : ''
            }
          />
        )
      case 'SpanKind':
        return <TableColumn key="SpanKind" title={_$('Span类型')} width="100" />
      case 'AISpanKind':
        return <TableColumn key="AISpanKind" title={_$('AI Span类型')} width="100" />
      case 'StatusCode':
        return (
          <TableColumn
            key="StatusCode"
            title={_$('状态码')}
            width="100"
            slotTemplate={([data]) => {
              return renderStatusCode(data?.StatusCode || '')
            }}
          />
        )
      case 'Operation':
        return (
          <TableColumn
            key="Operation"
            title={_$('AI操作名称')}
            width="100"
            slotTemplate={([data]) => {
              return <div>{data?.Operation || '-'}</div>
            }}
          />
        )
      case 'ServiceName':
        return (
          <TableColumn
            key="ServiceName"
            title={_$('服务名')}
            width="150"
            slotTemplate={([data]) => {
              return <TruncatedValueWithTooltip value={data.ServiceName || '-'} />
            }}
          />
        )
      case 'SessionId':
        return (
          <TableColumn
            key="SessionId"
            title={_$('会话ID')}
            fixed="right"
            width="150"
            slotTemplate={([data]) => {
              return data.SessionId ? (
                <a
                  onClick={e => {
                    e.preventDefault()
                    toSessionDetail(data)
                  }}
                >
                  {data.SessionId}
                </a>
              ) : (
                '-'
              )
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className={className}>
      <Tabs
        type="flat-card"
        value={tab}
        onChangeValue={v => {
          replaceSearchParams(history, location, { traceTableTab: v })
          setTab(v as TableTabType)
          setTraceSearch({ ...traceSearch, Page: 1 })
        }}
      >
        <Tab value="rootSpan">{_$('Root Span列表')}</Tab>
        <Tab value="span">{_$('Span列表')}</Tab>
      </Tabs>
      <div className="trace-table-header">
        <ExportTrace checkedRow={checkedRow} traceData={traceData?.list || []} tab={tab} />
        <ColumnConfigDropdown
          items={TABLE_HEADERS.map(item => ({ key: item.key, label: item.value }))}
          selectedKeys={checkedHeader}
          orderKeys={headerOrder}
          onChange={(nextSelectedKeys, nextOrderKeys) => {
            setCheckedHeader(nextSelectedKeys)
            setHeaderOrder(nextOrderKeys)
          }}
        />
      </div>
      <Table
        data={traceData?.list || []}
        loading={loading}
        rowKey={data => `${data.ServiceName}-${data.TraceId}-${data.SpanId}`}
        checkedKeys={checkedRow}
        onChangeCheckedKeys={keys => {
          setCheckedRow(keys)
        }}
        fixHeader={tableHeight}
        resizable={true}
        animation={false}
        sort={tableSort}
        onChangeSort={sort => {
          setTableSort(sort)
        }}
      >
        {visibleHeaders.map(header => renderTableColumn(header.key))}
      </Table>
      {traceData !== undefined ? (
        <Pagination
          style={{ marginTop: 16 }}
          value={traceSearch.Page}
          limit={traceSearch.PageSize}
          total={traceData?.total || 0}
          size="small"
          onChangeValue={value => {
            setTraceSearch({
              ...traceSearch,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setTraceSearch({
              ...traceSearch,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(TraceTable)`
  width: 100%;
  overflow: hidden;
  position: relative;
  padding-left: 12px;
  border-left: 1px solid ${variable.borderColor};

  .k-tabs {
    display: inline-block;
  }
  .trace-span-chart-wrap {
    margin-top: 16px;
    width: 100%;
    height: 280px;
  }
  .trace-table-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: absolute;
    right: 0;
    top: 4px;
  }
  .k-table {
    margin-top: 8px;
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
`
